import { ChevronDown, ExternalLink } from "lucide-react"
import { useEffect, useState } from "react"

import { SOURCE_FOCUS_EVENT, sourceRowId, type SourceFocusDetail } from "@/lib/citations"
import { groupSources, sourceGroupKey, type SourceGroupKey } from "@/lib/sources"
import { formatDate } from "@/lib/text"
import { usePreview } from "@/providers/PreviewProvider"
import type { Source } from "@/types/api"

const FILE_BADGE: Record<string, string> = {
  DOCX: "bg-arctic text-[#2b2b2b]",
  PDF: "bg-soft-green text-[#2b2b2b]",
  PPTX: "bg-arctic-deep text-[#2b2b2b]",
  XLSX: "bg-soft-green-deep text-[#2b2b2b]",
  IMG: "bg-overlay text-fg2",
  FILE: "bg-overlay text-fg2",
  MAIL: "bg-amber-tint text-fg1",
  CHAT: "bg-amber-tint text-fg1",
}

/** Best-effort file kind from the title or URL extension — used for the type chip. */
function fileKind(source: Source): keyof typeof FILE_BADGE {
  if (source.kind === "mail") return "MAIL"
  if (source.kind === "chat") return "CHAT"
  const hay = `${source.url ?? ""} ${source.title}`.toLowerCase()
  const ext = hay.match(/\.([a-z0-9]{2,4})(?:[?#]|\s|$)/)?.[1]
  if (!ext) return "FILE"
  if (ext === "docx" || ext === "doc") return "DOCX"
  if (ext === "pdf") return "PDF"
  if (ext === "pptx" || ext === "ppt") return "PPTX"
  if (ext === "xlsx" || ext === "xls" || ext === "csv") return "XLSX"
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "IMG"
  return "FILE"
}

/** "Sites / <name>" hint extracted from a SharePoint URL, if present. */
function siteHint(url?: string): string {
  if (!url) return ""
  try {
    const match = decodeURIComponent(url).match(/\/sites\/([^/]+)/i)
    return match ? `Sites / ${match[1].replace(/[-_]+/g, " ")}` : ""
  } catch {
    return ""
  }
}

/** SharePoint file sources carry drive_id/item_id; external links (wiki, Board help) don't
 * and can't be embedded in the in-app preview iframe (X-Frame-Options), so open those directly. */
function openSource(openPreview: ReturnType<typeof usePreview>["openPreview"], source: Source): void {
  if (source.kind || (!source.drive_id && !source.item_id)) {
    window.open(source.url, "_blank", "noopener,noreferrer")
    return
  }
  openPreview({ url: source.url!, title: source.title, driveId: source.drive_id, itemId: source.item_id })
}

function SourceRow({
  source,
  number,
  messageId,
  last,
}: {
  source: Source
  number: number
  messageId?: string
  last: boolean
}) {
  const { openPreview } = usePreview()
  const clickable = Boolean(source.url && source.url !== "#")
  const kind = fileKind(source)
  const subtitle = [siteHint(source.url), formatDate(source.last_modified)].filter(Boolean).join(" · ")

  return (
    <div
      id={messageId ? sourceRowId(messageId, number) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => openSource(openPreview, source) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter") openSource(openPreview, source)
            }
          : undefined
      }
      className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-border-subtle"} ${
        clickable ? "cursor-pointer transition-colors hover:bg-hover" : ""
      }`}
    >
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-orange text-[11px] font-bold text-white">
        {number}
      </span>
      <span className={`shrink-0 rounded-sm px-1.5 py-1 text-[10px] font-bold tracking-[0.03em] ${FILE_BADGE[kind]}`}>
        {kind}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-foreground" title={source.title}>
          {source.title}
        </div>
        {subtitle && <div className="truncate text-[11.5px] text-fg3">{subtitle}</div>}
      </div>
      {clickable && (
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open in new tab"
          className="shrink-0 text-fg4 transition-colors hover:text-orange-press"
        >
          <ExternalLink className="size-3.5" />
        </a>
      )}
    </div>
  )
}

export function SourceList({ sources, messageId }: { sources: Source[]; messageId?: string }) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<SourceGroupKey>>(new Set())

  // A citation badge may target a row inside a collapsed group: expand that group.
  useEffect(() => {
    if (!messageId) return
    const onFocus = (e: Event) => {
      const detail = (e as CustomEvent<SourceFocusDetail>).detail
      if (detail?.messageId !== messageId) return
      const target = sources[detail.n - 1]
      if (!target) return
      const key = sourceGroupKey(target)
      setCollapsed((prev) => {
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
    window.addEventListener(SOURCE_FOCUS_EVENT, onFocus)
    return () => window.removeEventListener(SOURCE_FOCUS_EVENT, onFocus)
  }, [messageId, sources])

  if (!sources.length) return null

  const groups = groupSources(sources)
  const grouped = groups.length > 1

  const toggle = (key: SourceGroupKey) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border bg-card shadow-card">
      <div className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
        <span className="size-2.5 shrink-0 bg-orange" />
        <span className="text-[12px] font-bold tracking-[0.06em] text-fg2">SOURCES</span>
        <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-sm bg-arctic px-1.5 text-[11px] font-bold text-[#2b2b2b]">
          {sources.length}
        </span>
        <span className="ml-auto text-[12px] text-fg3">{sources.some((s) => s.kind) ? "Documents, mail and chats" : "Grounded in your documents"}</span>
      </div>

      <div className="flex flex-col">
        {groups.map((group, gi) => {
          const open = !grouped || !collapsed.has(group.key)
          const bodyId = `src-group-${messageId ?? "x"}-${group.key}`
          return (
            <div key={group.key} className="flex flex-col">
              {grouped && (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={bodyId}
                  onClick={() => toggle(group.key)}
                  className={`flex w-full items-center gap-2 bg-elevated px-4 py-2 text-left transition-colors hover:bg-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-orange ${
                    gi > 0 ? "border-t border-border-subtle" : ""
                  } ${open ? "border-b border-border-subtle" : ""}`}
                >
                  <ChevronDown className={`size-3.5 shrink-0 text-fg3 transition-transform ${open ? "" : "-rotate-90"}`} />
                  <span className="text-[12px] font-bold tracking-[0.04em] text-fg2">{group.label}</span>
                  <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-sm bg-arctic px-1.5 text-[11px] font-bold text-[#2b2b2b]">
                    {group.items.length}
                  </span>
                </button>
              )}
              {open && (
                <div id={bodyId} className="flex flex-col">
                  {group.items.map(({ source, number }, ii) => (
                    <SourceRow
                      key={`${source.title}-${number}`}
                      source={source}
                      number={number}
                      messageId={messageId}
                      last={ii === group.items.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
